import { useState, useEffect } from "react";
import { useUser } from "@clerk/clerk-react";
import { useSearchParams } from "react-router-dom";
import { DashboardLayout } from "@/components/dashboard/dashboard-layout";
import { MessageList } from "@/components/messaging/MessageList";
import { MessageThread } from "@/components/messaging/MessageThread";
import { NewMessageDialog } from "@/components/messaging/NewMessageDialog";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { MessageSquare, AlertCircle } from "lucide-react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Id } from "../../../../../convex/_generated/dataModel";
import { toast } from "sonner";

export default function MessagesPage() {
  const [selectedConversationId, setSelectedConversationId] = useState<string>("");
  const [isNewMessageOpen, setIsNewMessageOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Fetch conversations and contacts
  const { user, isLoaded: userLoaded } = useUser();
  const conversations = useQuery(api.messages.getConversations, user ? {} : "skip");
  const contacts = useQuery(api.messages.getContacts, user ? {} : "skip");
  const userData = useQuery(api.users.getCurrentUser, user ? {} : "skip");

  // Handle conversation pre-selection from URL parameters
  useEffect(() => {
    const conversationParam = searchParams.get("conversation");
    const applicationParam = searchParams.get("application");

    if (conversationParam && conversations) {
      // Check if the conversation exists in the user's conversations
      const conversationExists = conversations.some((conv: any) => conv._id === conversationParam);
      if (conversationExists) {
        setSelectedConversationId(conversationParam);
        // Clear the URL parameter after setting the conversation
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete("conversation");
        setSearchParams(newSearchParams, { replace: true });
      }
    } else if (applicationParam && conversations) {
      // Find conversation by application ID
      const applicationConversation = conversations.find(
        (conv: any) => conv.type === "application" && conv.applicationId === applicationParam
      );
      if (applicationConversation) {
        setSelectedConversationId(applicationConversation._id);
        // Clear the URL parameter after setting the conversation
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete("application");
        setSearchParams(newSearchParams, { replace: true });
      }
    }
  }, [searchParams, conversations, setSearchParams]);

  // Loading states
  const isLoadingConversations = conversations === undefined;
  const isLoadingContacts = contacts === undefined;
  const isLoadingUserData = userData === undefined;
  const isLoading = !userLoaded || isLoadingConversations || isLoadingContacts || isLoadingUserData;

  // Fetch messages for selected conversation
  const messages =
    useQuery(
      api.messages.getMessages,
      selectedConversationId
        ? { conversationId: selectedConversationId as Id<"conversations"> }
        : "skip"
    ) || [];

  // Mutations
  const sendMessage = useMutation(api.messages.sendMessage);
  const createConversation = useMutation(api.messages.createConversation);
  const findOrCreateOrganizationConversation = useMutation(
    api.messages.findOrCreateOrganizationConversation
  );
  const markAsRead = useMutation(api.messages.markAsRead);

  const handleSelectContact = (contactId: string) => {
    // For now, we'll use the contact ID as conversation ID
    // In a real implementation, you'd find or create a conversation
    setSelectedConversationId(contactId);
  };

  const handleSendMessage = async (content: string, attachments?: File[]) => {
    if (!selectedConversationId || !content.trim()) return;

    try {
      await sendMessage({
        conversationId: selectedConversationId as Id<"conversations">,
        content,
        messageType: "text",
        // TODO: Handle file attachments when file upload is implemented
      });

      // Mark conversation as read after sending
      await markAsRead({
        conversationId: selectedConversationId as Id<"conversations">,
      });
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message. Please try again.");
    }
  };

  const handleCreateMessage = async (contactId: string) => {
    try {
      // Determine if this is a business contact or team member
      const contact = (contacts || []).find((c: any) => c.id === contactId);
      if (!contact) {
        toast.error("Contact not found. Please try again.");
        return;
      }

      let conversationId: string;

      if (contact.type === "team") {
        // Direct conversation with team member
        const participantIds = [contactId as Id<"users">];
        conversationId = await createConversation({
          type: "direct",
          participantIds,
        });
      } else if (contact.type === "client" || contact.type === "lead") {
        // Business conversation
        const businessId = contactId as Id<"businesses">;
        conversationId = await createConversation({
          type: "business",
          participantIds: [],
          businessId,
        });
      } else if (
        contact.type === "prime-contractor" ||
        contact.type === "subcontractor" ||
        contact.type === "business-contact"
      ) {
        // Cross-organization conversation
        if (!contact.organizationId) {
          toast.error("Organization information not found for this contact.");
          return;
        }

        conversationId = await findOrCreateOrganizationConversation({
          targetOrgId: contact.organizationId as Id<"organizations">,
          context: {
            type: "opportunity",
            id: "manual",
            title: `Conversation with ${contact.name}`,
          },
        });
      } else {
        toast.error(`Unsupported contact type: ${contact.type}`);
        return;
      }

      setSelectedConversationId(conversationId);
      setIsNewMessageOpen(false);
      // Success message will be shown by NewMessageDialog
    } catch (error) {
      console.error("Error creating conversation:", error);
      // Re-throw error so NewMessageDialog can handle it
      throw error;
    }
  };

  // Convert conversations to contact format for MessageList component
  const conversationContacts = (conversations || []).map((conv: any) => {
    // Base contact structure
    const baseContact = {
      id: conv._id,
      lastMessage: conv.lastMessage
        ? {
            content: conv.lastMessage.content,
            timestamp: conv.lastMessage.timestamp,
            isUnread: conv.unreadCount > 0,
          }
        : undefined,
      // Only include unreadCount if it's greater than 0
      ...(conv.unreadCount && conv.unreadCount > 0 ? { unreadCount: conv.unreadCount } : {}),
      isPinned: conv.isPinned || false,
      reference: conv.context
        ? {
            type: conv.context.type as "bid" | "business" | "opportunity" | "application",
            id: conv.context.id,
            name: conv.context.name,
          }
        : undefined,
    };

    // For business conversations, use business info
    if (conv.type === "business" && conv.context) {
      return {
        ...baseContact,
        name: conv.context.name,
        avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${conv.context.name}`,
        type: conv.context.type || "client",
      };
    }

    // For direct conversations, use the other participant's info
    const otherParticipant = (conv.participants || []).find((p: any) => p.id !== userData?._id);
    if (otherParticipant) {
      return {
        ...baseContact,
        name: otherParticipant.name,
        avatar:
          otherParticipant.avatar ||
          `https://api.dicebear.com/7.x/avataaars/svg?seed=${otherParticipant.name}`,
        type: "team" as const,
      };
    }

    return {
      ...baseContact,
      name: conv.title || "Unknown Conversation",
      avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=unknown",
      type: "team" as const,
      lastMessage: conv.lastMessage,
    };
  });

  const selectedContact = conversationContacts?.find((c: any) => c.id === selectedConversationId);

  // Get conversation context for reference
  const selectedConversation = (conversations || []).find(
    (c: any) => c._id === selectedConversationId
  );
  const reference = selectedConversation?.context
    ? {
        type: selectedConversation.context.type as
          | "bid"
          | "service"
          | "opportunity"
          | "location"
          | "task"
          | "application",
        id: selectedConversation.context.id,
        title: selectedConversation.context.name,
      }
    : undefined;

  // Prepare conversation data for actions menu
  const conversationData = selectedConversation
    ? {
        id: selectedConversation._id,
        isRead: selectedConversation.unreadCount === 0,
        isPinned: selectedConversation.isPinned || false,
        isArchived: selectedConversation.isArchived || false,
      }
    : undefined;

  // Function to refresh conversations after actions
  const handleConversationUpdate = () => {
    // The useQuery will automatically refetch, but we can force it if needed
    // For now, the reactive nature of Convex will handle updates
  };

  // Show loading state
  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="h-[calc(100vh-10rem)]">
          <div className="grid h-full grid-cols-1 gap-0 overflow-hidden rounded-lg border bg-white md:grid-cols-[350px_1fr]">
            {/* Loading skeleton for message list */}
            <div className="border-r bg-white">
              <div className="border-b p-4">
                <Skeleton className="mb-4 h-6 w-24" />
                <Skeleton className="mb-3 h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
              <div className="space-y-4 p-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <div className="flex-1">
                      <Skeleton className="mb-2 h-4 w-32" />
                      <Skeleton className="h-3 w-48" />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Loading skeleton for message thread */}
            <div className="flex h-full items-center justify-center bg-gray-50">
              <div className="text-center">
                <Skeleton className="mx-auto mb-2 h-8 w-48" />
                <Skeleton className="mx-auto h-4 w-64" />
              </div>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Show error state if user is not loaded properly
  if (!user || !userData) {
    return (
      <DashboardLayout>
        <div className="h-[calc(100vh-10rem)]">
          <div className="flex h-full items-center justify-center">
            <EmptyState
              icon={AlertCircle}
              title="Unable to load messages"
              description="Please try refreshing the page or contact support if the issue persists"
            />
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="h-[calc(100vh-10rem)] w-full max-w-full">
        <div className="h-full w-full overflow-hidden rounded-lg border bg-white">
          {/* Mobile: Show only one panel at a time */}
          <div className="h-full md:hidden">
            {selectedContact ? (
              <div className="flex h-full flex-col">
                {/* Mobile header with back button */}
                <div className="flex items-center gap-3 border-b bg-white p-4">
                  <button
                    onClick={() => setSelectedConversationId("" as any)}
                    className="rounded-lg p-2 hover:bg-gray-100"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-200">
                      {selectedContact.avatar ? (
                        <img
                          src={selectedContact.avatar}
                          alt={selectedContact.name}
                          className="h-8 w-8 rounded-full"
                        />
                      ) : (
                        <span className="text-sm font-medium">
                          {selectedContact.name.charAt(0)}
                        </span>
                      )}
                    </div>
                    <h2 className="font-semibold">{selectedContact.name}</h2>
                  </div>
                </div>
                {/* Message thread content */}
                <div className="flex-1">
                  <MessageThread
                    recipient={{
                      id: selectedContact.id,
                      name: selectedContact.name,
                      avatar: selectedContact.avatar,
                      type: selectedContact.type,
                    }}
                    messages={messages || []}
                    onSendMessage={handleSendMessage}
                    reference={reference}
                    conversation={conversationData}
                    onConversationUpdate={handleConversationUpdate}
                  />
                </div>
              </div>
            ) : (
              <MessageList
                contacts={conversationContacts}
                onSelectContact={handleSelectContact}
                selectedContactId={selectedConversationId}
                onNewMessage={() => setIsNewMessageOpen(true)}
              />
            )}
          </div>

          {/* Desktop: Show both panels side by side */}
          <div className="hidden h-full w-full gap-0 overflow-hidden md:grid md:grid-cols-[350px_1fr]">
            <MessageList
              contacts={conversationContacts}
              onSelectContact={handleSelectContact}
              selectedContactId={selectedConversationId}
              onNewMessage={() => setIsNewMessageOpen(true)}
            />

            {selectedContact ? (
              <MessageThread
                recipient={{
                  id: selectedContact.id,
                  name: selectedContact.name,
                  avatar: selectedContact.avatar,
                  type: selectedContact.type,
                }}
                messages={messages || []}
                onSendMessage={handleSendMessage}
                reference={reference}
                conversation={conversationData}
                onConversationUpdate={handleConversationUpdate}
              />
            ) : (
              <div className="flex h-full items-center justify-center bg-gray-50">
                <EmptyState
                  icon={MessageSquare}
                  title="No conversation selected"
                  description="Select a conversation or start a new one"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <NewMessageDialog
        isOpen={isNewMessageOpen}
        onClose={() => setIsNewMessageOpen(false)}
        onCreateMessage={handleCreateMessage}
        contacts={contacts || []}
      />
    </DashboardLayout>
  );
}
